import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import {
  FaHeart,
  FaCheck,
  FaSearch,
  FaBed,
  FaBath,
  FaShareAlt,
  FaTrashAlt,
  FaCompass,
  FaPhoneAlt,
  FaBuilding,
  FaLock,
  FaSignInAlt,
} from "react-icons/fa";
import { IoLocationSharp, IoSparkles, IoShieldCheckmark } from "react-icons/io5";
import { BiChevronRight } from "react-icons/bi";
import { setModelShow } from "../../redux/athentication/Athentication";
import {
  getShortlistedProperties,
  removeShortlistProperty,
  clearAllShortlist,
  toggleShortlistProperty,
} from "../../utils/shortlistUtils";
import PropertyDetailsModel from "../home/latestProperty/PropertyDetailsModel";
import "./ShortListed.css";

// Sample verified properties to load if empty or requested
const SAMPLE_PROPERTIES = [
  {
    _id: "sample_aparna_1",
    propertyID: "MGH-10492",
    apartment: "Aparna Serene Park",
    visiblePrice: "8.02 - 9.37 Cr",
    expectedPrice: "85000000",
    noofBedrooms: 4,
    noofBathrooms: 4,
    carpetArea: 3850,
    locality: "Kondapur",
    city: "Hyderabad",
    state: "Telangana",
    superCategory: "Residential",
    subCategory: "Apartment",
    category: "Sell",
    furnishing: "Semi-Furnished",
    propertyFacing: "East",
    propertyOnFloor: "14",
    noofFloor: "24",
    postUserName: "Aparna Constructions",
    postUserMobile: "9160213545",
    postUserPosition: "Builder",
    DeskImg: ["/assets/images/hero_banner_new.png"],
  },
  {
    _id: "sample_autuman_2",
    propertyID: "MGH-17538",
    apartment: "Autuman villas",
    visiblePrice: "2.15 Cr",
    expectedPrice: "21500000",
    noofBedrooms: 3,
    noofBathrooms: 3,
    carpetArea: 2450,
    locality: "Thukkuguda",
    city: "Hyderabad",
    state: "Telangana",
    superCategory: "Residential",
    subCategory: "Villa",
    category: "Sell",
    furnishing: "Furnished",
    propertyFacing: "North-East",
    propertyOnFloor: "G+1",
    noofFloor: "2",
    postUserName: "V laxman",
    postUserMobile: "9160213545",
    postUserPosition: "Owner",
    DeskImg: ["/assets/images/why_choose_villa.jpg"],
  },
  {
    _id: "sample_prestige_3",
    propertyID: "MGH-18921",
    apartment: "Prestige High Fields",
    visiblePrice: "1.80 Cr",
    expectedPrice: "18000000",
    noofBedrooms: 3,
    noofBathrooms: 3,
    carpetArea: 1980,
    locality: "Gachibowli",
    city: "Hyderabad",
    state: "Telangana",
    superCategory: "Residential",
    subCategory: "Apartment",
    category: "Sell",
    furnishing: "Unfurnished",
    propertyFacing: "East",
    propertyOnFloor: "8",
    noofFloor: "32",
    postUserName: "Rajesh Kumar",
    postUserMobile: "9160213545",
    postUserPosition: "Agent",
    DeskImg: ["/assets/images/hero_slide_2.jpg"],
  },
];

const ShortListed = ({ isDashboardView = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuth, loginData } = useSelector((store) => store.Athentication);

  // States
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedPropForContact, setSelectedPropForContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  // Load items from localStorage
  const refreshItems = () => {
    const list = getShortlistedProperties();
    setItems(list);
  };

  useEffect(() => {
    refreshItems();

    // Listen for cross-component shortlist updates
    const handleStorageUpdate = () => {
      refreshItems();
    };

    window.addEventListener("mgh_shortlist_updated", handleStorageUpdate);
    window.addEventListener("storage", handleStorageUpdate);

    return () => {
      window.removeEventListener("mgh_shortlist_updated", handleStorageUpdate);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  // Remove single property
  const handleRemove = (e, propId, propTitle) => {
    e.preventDefault();
    e.stopPropagation();

    removeShortlistProperty(propId);
    refreshItems();

    toast.info(`"${propTitle || "Property"}" removed from shortlist`, {
      position: "top-center",
      autoClose: 1800,
    });
  };

  // Clear all shortlisted properties
  const handleClearAll = () => {
    if (items.length === 0) return;
    if (window.confirm("Are you sure you want to remove all shortlisted properties?")) {
      clearAllShortlist();
      refreshItems();
      toast.info("All shortlisted properties removed.", {
        position: "top-center",
        autoClose: 1800,
      });
    }
  };

  // Load sample properties for testing if empty
  const handleLoadSamples = () => {
    SAMPLE_PROPERTIES.forEach((prop) => {
      toggleShortlistProperty(prop);
    });
    refreshItems();
    toast.success("Loaded verified properties to your shortlist!", {
      position: "top-center",
      autoClose: 1800,
    });
  };

  // Contact / Inquire Modal
  const handleOpenContact = (e, prop) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPropForContact(prop);
    setContactModalOpen(true);
  };

  // Share Property link
  const handleShare = async (e, prop) => {
    e.preventDefault();
    e.stopPropagation();

    const apartName = prop.apartment || prop.apartmentName || "property";
    const propId = prop.propertyID || prop._id;
    const shareUrl = `${window.location.origin}/property-info/${encodeURIComponent(apartName)}/${propId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: prop.apartment || "Property on EwRealty",
          text: `Check out this shortlisted property: ${prop.apartment}`,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // Fallback to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Property link copied to clipboard!", {
        position: "top-center",
        autoClose: 1600,
      });
    } catch (err) {
      toast.info(`Link: ${shareUrl}`);
    }
  };

  // Filtered & Sorted Properties List
  const filteredProperties = useMemo(() => {
    let result = [...items];

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((p) => {
        const title = (p.apartment || p.apartmentName || p.propertyTitle || "").toLowerCase();
        const loc = (p.locality || "").toLowerCase();
        const city = (p.city || "").toLowerCase();
        return title.includes(q) || loc.includes(q) || city.includes(q);
      });
    }

    // Category filter
    if (selectedCategory !== "All") {
      result = result.filter((p) => {
        if (selectedCategory === "Residential") {
          return p.superCategory === "Residential" || !p.superCategory;
        }
        if (selectedCategory === "Commercial") {
          return p.superCategory === "Commercial";
        }
        if (selectedCategory === "Buy") {
          return p.category === "Sell" || p.category === "Buy" || !p.category;
        }
        if (selectedCategory === "Rent") {
          return p.category === "Rent";
        }
        return true;
      });
    }

    // Sorting
    if (sortBy === "price_asc") {
      result.sort((a, b) => {
        const pA = parseFloat(a.expectedPrice || a.visiblePrice) || 0;
        const pB = parseFloat(b.expectedPrice || b.visiblePrice) || 0;
        return pA - pB;
      });
    } else if (sortBy === "price_desc") {
      result.sort((a, b) => {
        const pA = parseFloat(a.expectedPrice || a.visiblePrice) || 0;
        const pB = parseFloat(b.expectedPrice || b.visiblePrice) || 0;
        return pB - pA;
      });
    } else if (sortBy === "bhk") {
      result.sort((a, b) => (Number(b.noofBedrooms) || 0) - (Number(a.noofBedrooms) || 0));
    }

    return result;
  }, [items, searchQuery, selectedCategory, sortBy]);

  return (
    <div className={`shortlist-page-wrapper ${isDashboardView ? "is-dashboard" : ""}`}>
      <div className="shortlist-container">
        {/* Breadcrumb (Only in standalone page view) */}
        {!isDashboardView && (
          <div className="shortlist-breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span className="current">Shortlisted Properties</span>
          </div>
        )}

        {/* Activity Tabs matching Recent Searches & Viewed */}
        {!isDashboardView && (
          <div className="shortlist-activity-tabs">
            <Link to="/recentelysearched" className="shortlist-tab-item">
              <span>Recent Searches</span>
            </Link>
            <Link to="/viewed" className="shortlist-tab-item">
              <span>Recently Viewed</span>
            </Link>
            <Link to="/shortlisted" className="shortlist-tab-item active">
              <span>Shortlisted</span>
              <span className="shortlist-tab-badge">{items.length}</span>
            </Link>
            <Link to="/contacted" className="shortlist-tab-item">
              <span>Contacted</span>
            </Link>
          </div>
        )}

        {/* Auth status banner */}
        {!isAuth ? (
          <div className="shortlist-auth-banner" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
            <div className="shortlist-auth-info" style={{ color: "#92400e" }}>
              <IoShieldCheckmark style={{ color: "#d97706" }} />
              <span>
                <strong>Guest Mode:</strong> Properties shortlisted on this device are saved in your browser.
              </span>
            </div>
            <button
              type="button"
              className="shortlist-user-badge"
              style={{ cursor: "pointer", background: "#f59e0b", color: "#fff", border: "none" }}
              onClick={() => navigate("/login")}
            >
              <FaSignInAlt style={{ marginRight: "4px" }} /> Sign In to Sync
            </button>
          </div>
        ) : (
          <div className="shortlist-auth-banner" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
            <div className="shortlist-auth-info" style={{ color: "#166534" }}>
              <IoShieldCheckmark style={{ color: "#16a34a" }} />
              <span>
                <strong>Logged In:</strong> Shortlisted homes are synced with your account (
                {loginData?.name || loginData?.mobile || "User"}).
              </span>
            </div>
            <span className="shortlist-user-badge">
              <FaCheck /> Synced
            </span>
          </div>
        )}

            {/* Header Card */}
            <div className="shortlist-header-card">
              <div className="shortlist-header-top">
                <div className="shortlist-title-wrap">
                  <div className="shortlist-heart-icon-badge">
                    <FaHeart />
                  </div>
                  <div>
                    <h1 className="shortlist-title">Shortlisted Properties</h1>
                    <p className="shortlist-subtitle">
                      {items.length === 0
                        ? "Manage your saved properties in one convenient place."
                        : `You have saved ${items.length} ${items.length === 1 ? "property" : "properties"
                        } to review.`}
                    </p>
                  </div>
                </div>

                <div className="shortlist-header-actions">
                  {items.length > 0 && (
                    <button
                      className="shortlist-clear-btn"
                      onClick={handleClearAll}
                      title="Clear all saved properties"
                    >
                      <FaTrashAlt />
                      <span>Clear All</span>
                    </button>
                  )}
                  <Link to="/search" className="shortlist-explore-btn">
                    <FaCompass />
                    <span>Explore Properties</span>
                  </Link>
                </div>
              </div>

              {/* Toolbar: Search, Category Filters, Sort Selector */}
              {items.length > 0 && (
                <div className="shortlist-toolbar">
                  <div className="shortlist-search-box">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Search in shortlisted by name or location..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="shortlist-search-input"
                    />
                  </div>

                  <div className="shortlist-filter-pills">
                    {["All", "Buy", "Rent", "Residential", "Commercial"].map((cat) => (
                      <button
                        key={cat}
                        className={`shortlist-pill ${selectedCategory === cat ? "active" : ""}`}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  <select
                    className="shortlist-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="recent">Sort: Recently Added</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="bhk">Bedrooms: Max to Min</option>
                  </select>
                </div>
              )}
            </div>

            {/* Content Body */}
            {filteredProperties.length > 0 ? (
              <div className="shortlist-grid">
                {filteredProperties.map((prop, idx) => {
                  const propId = prop.propertyID || prop._id || idx;
                  const apartName =
                    prop.apartment || prop.apartmentName || prop.propertyTitle || "Property";
                  const detailLink = `/property-info/${encodeURIComponent(apartName)}/${propId}`;

                  const priceText = prop.visiblePrice
                    ? `₹ ${prop.visiblePrice}`
                    : prop.expectedPrice
                      ? `₹ ${prop.expectedPrice}`
                      : "Price on Request";

                  const bhkText = prop.noofBedrooms
                    ? `${prop.noofBedrooms} BHK`
                    : "3 BHK";

                  const locText =
                    [prop.locality, prop.city].filter(Boolean).join(", ") ||
                    "Hyderabad, Telangana";

                  const imageSrc =
                    (prop.DeskImg && prop.DeskImg[0]) ||
                    (prop.photos && prop.photos[0]) ||
                    prop.thumbnail ||
                    "/assets/images/why_choose_villa.jpg";

                  return (
                    <div key={propId} className="shortlist-card">
                      {/* Card Media Banner */}
                      <div className="shortlist-card-media">
                        <Link to={detailLink}>
                          <img
                            src={imageSrc}
                            alt={apartName}
                            className="shortlist-card-img"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/assets/images/noimage1.png";
                            }}
                          />
                        </Link>

                        <div className="shortlist-badge-verified">
                          <FaCheck />
                          <span>Verified</span>
                        </div>

                        <div className="shortlist-badge-category">
                          {prop.subCategory || prop.superCategory || "Apartment"}
                        </div>

                        <button
                          className="shortlist-card-remove-btn"
                          onClick={(e) => handleRemove(e, propId, apartName)}
                          title="Remove from shortlist"
                        >
                          <FaHeart />
                        </button>
                      </div>

                      {/* Card Body Details */}
                      <div className="shortlist-card-body">
                        <div className="shortlist-card-price-row">
                          <h3 className="shortlist-card-price">{priceText}</h3>
                          <span className="shortlist-card-bhk">{bhkText}</span>
                        </div>

                        <h4 className="shortlist-card-title" title={apartName}>
                          <Link to={detailLink} style={{ color: "inherit", textDecoration: "none" }}>
                            {apartName}
                          </Link>
                        </h4>

                        <div className="shortlist-card-location">
                          <IoLocationSharp />
                          <span>{locText}</span>
                        </div>

                        {/* Quick Specs Chips */}
                        <div className="shortlist-specs-row">
                          <div className="shortlist-spec-item">
                            <FaBuilding />
                            <span>{prop.carpetArea || 1450} sq.ft</span>
                          </div>
                          <div className="shortlist-spec-dot"></div>
                          <div className="shortlist-spec-item">
                            <FaBed />
                            <span>{prop.noofBedrooms || 3} Beds</span>
                          </div>
                          <div className="shortlist-spec-dot hide-on-mobile"></div>
                          <div className="shortlist-spec-item hide-on-mobile">
                            <FaBath />
                            <span>{prop.noofBathrooms || 2} Baths</span>
                          </div>
                          <div className="shortlist-spec-dot hide-on-mobile"></div>
                          <div className="shortlist-spec-item hide-on-mobile">
                            <span>{prop.furnishing || "Semi-Furnished"}</span>
                          </div>
                        </div>

                        {/* Card Actions */}
                        <div className="shortlist-card-actions">
                          <Link to={detailLink} className="shortlist-view-btn">
                            <span className="shortlist-btn-text-desktop">View Details</span>
                            <span className="shortlist-btn-text-mobile">View</span>
                            <BiChevronRight className="shortlist-btn-icon-desktop" />
                          </Link>

                          <button
                            className="shortlist-contact-btn"
                            onClick={(e) => handleOpenContact(e, prop)}
                            title="Contact Seller"
                          >
                            <FaPhoneAlt />
                            <span className="shortlist-btn-text-desktop">Contact</span>
                          </button>

                          <button
                            className="shortlist-share-btn"
                            onClick={(e) => handleShare(e, prop)}
                            title="Share property"
                          >
                            <FaShareAlt />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : items.length === 0 ? (
              /* Empty State View */
              <div className="shortlist-empty-card">
                <div className="shortlist-empty-graphic">
                  <FaHeart />
                </div>
                <h2 className="shortlist-empty-title">You haven’t shortlisted anything yet!</h2>
                <p className="shortlist-empty-desc">
                  Browse top verified homes and apartments across Hyderabad, then click the heart icon on
                  any card to keep them handy here for easy comparison.
                </p>
                <div className="shortlist-empty-buttons">
                  <Link to="/search" className="shortlist-explore-btn" style={{ padding: "10px 24px" }}>
                    <FaCompass />
                    <span>Explore Properties</span>
                  </Link>

                </div>
              </div>
            ) : (
              /* No search matches state */
              <div className="shortlist-empty-card" style={{ padding: "40px 20px" }}>
                <h3 className="shortlist-empty-title" style={{ fontSize: "18px" }}>
                  No shortlisted properties match "{searchQuery}"
                </h3>
                <p className="shortlist-empty-desc">Try searching with a different keyword or filter.</p>
                <button
                  className="shortlist-sample-btn"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("All");
                  }}
                >
                  Reset Filters
                </button>
              </div>
            )}
      </div>

      {/* Contact / Inquire Modal */}
      {selectedPropForContact && (
        <Modal
          isOpen={contactModalOpen}
          toggle={() => setContactModalOpen(!contactModalOpen)}
          className="modalmainnew"
        >
          <div className="model-wrappernew">

            <ModalBody style={{ padding: 0 }}>
              <PropertyDetailsModel
                closePropdet={() => setContactModalOpen(false)}
                propertyprop={selectedPropForContact}
                changeView={() => { }}
              />
            </ModalBody>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ShortListed;